import React, { useContext, useRef, useState } from "react";
import { useEffect } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useHistory } from "react-router-dom";
import { useParams, useRouteMatch } from "react-router";

import useQuery from "../../utils/useQuery";
import carouselImg from "../../assets/icons/svg/Icon awesome-angle-right.svg";
import { getCatchupsByLabel } from "../../api/catchup/catchup";
import MiniCatchupDetail from "../../components/miniCatchupDetail/MiniCatchupDetail";
import { flowCopy } from "../../utils/flowCopy";
import VodItem from "../../components/vodItem";

const CatchupGridView = ({ dontUseGrid }) => {
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  // Mini detail hover state management (like VodGrid)
  const [showMiniCatchupDetail, setShowMiniCatchupDetail] = useState(false);
  
  // Refs for hover positioning (like VodGrid)
  const previewData = useRef({});
  const isFirstRow = useRef(false);
  const isRight = useRef(false);
  const isLeft = useRef(false);

  const { menuId, categoryId } = useParams();
  
  // Extract the actual label ID from the categoryId if it's in the format "catchups_176"
  let actualLabelId = categoryId;
  if (categoryId && categoryId.includes("catchups_")) {
    actualLabelId = categoryId.split("_")[1]; // Extract "176" from "catchups_176"
  }
  
  // Validate that we have a numeric label ID
  if (!actualLabelId || isNaN(actualLabelId)) {
    console.error("Invalid label ID for catchup:", { categoryId, actualLabelId });
    actualLabelId = "176"; // Fallback to the default catchup label ID
  }
  
  const columnCode = actualLabelId;
  const columnName = "Watch Again"; // Default name, could be fetched from API

  console.log("🔍 DEBUG: CatchupGridView params:", { menuId, categoryId, columnCode });

  const history = useHistory();

  const pageRef = useRef(0);
  const generation = useRef(0);
  const pending = useRef(false);
  const [error, setError] = useState(false);
  const itemsRef = useRef([]);
  const totalCountRef = useRef(0);

  let query = useQuery();
  let posterPosition = query.get("poster");

  let pageItemCount = 20; // request for other pages catchup count - match with request

  useEffect(() => {
    generation.current++;
    pending.current = false;
    pageRef.current = 0;
    itemsRef.current = [];
    totalCountRef.current = 0;
    setItems([]);
    setHasMore(true);
    setShowMiniCatchupDetail(false);
    getCatchupItems();
    return () => { generation.current++; };
  }, [columnCode]);

  const getCatchupItems = async () => {
    if (pending.current) return;
    pending.current = true;
    const version = generation.current;
    setError(false);
    try {
      const { data } = await getCatchupsByLabel(
        columnCode,
        pageRef.current,
        pageItemCount
      );
      
      if (version !== generation.current) return;
      pageRef.current++;

      let listItems = null;

      // Handle catchups from the response
      if (data.catchups?.length > 0) {
        listItems = data.catchups;
      }

      if (listItems) {
        itemsRef.current = itemsRef.current.concat(listItems);
        totalCountRef.current = data?.totalContents || listItems.length;
        setItems(itemsRef.current);
        setHasMore(data?.totalContents != null ? itemsRef.current.length < data.totalContents : listItems.length === pageItemCount);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      if (version === generation.current) { setError(true); setHasMore(false); }
    } finally {
      if (version === generation.current) pending.current = false;
    }
  };

  // Custom grid rendering (like VodGrid pattern)
  const renderedItems = items?.map((item, index) => {
    // Handle catchup content types
    return item?.type === "CATCHUPSERIES" || item?.type === "CATCHUPMOVIE" ? (
      <VodItem
        key={item?.id}
        item={item}
        index={index}
        itemsToShow={posterPosition == 7 ? 5 : 6}
        posterPosition={posterPosition}
        isCatchup={true}
        onHoverItem={(isShow, data) => {
          if (isShow) {
            previewData.current = data;
            isFirstRow.current =
              (posterPosition == 7 && index < 5) ||
              ((posterPosition == 6 || posterPosition == 1) && index < 6);
            isRight.current =
              (posterPosition == 7 && !((index + 1) % 5)) ||
              ((posterPosition == 6 || posterPosition == 1) &&
                !((index + 1) % 6));
            isLeft.current =
              (posterPosition == 7 && !(index % 5)) ||
              ((posterPosition == 6 || posterPosition == 1) && !(index % 6));
          }
          setShowMiniCatchupDetail(isShow);
        }}
      />
    ) : null;
  });

  return (
    <div className="cinema-grid-page">
      <div
        className="label-title"
        role="button" tabIndex={0} onKeyDown={(event) => { if (event.key === "Enter") history.goBack(); }}
        onClick={() => {
          history.goBack();
        }}
      >
        <img className="go-back-icon" src={carouselImg} />
        {columnName}
      </div>

      {error && <div role="alert">{flowCopy().loadError} <button onClick={getCatchupItems}>{flowCopy().retry}</button></div>}
      <InfiniteScroll
        dataLength={items?.length}
        next={() => {
          if (totalCountRef.current > items?.length) {
            getCatchupItems();
          }
        }}
        hasMore={hasMore}
      >
        {/* Custom grid wrapper (like VodGrid) */}
        <div className="content-grid-wrapper" style={{ marginTop: '34px' }}>
          <div className="content-grid">
            {renderedItems}
          </div>
        </div>
      </InfiniteScroll>

      {/* MiniCatchupDetail rendering (like VodGrid) */}
      {showMiniCatchupDetail ? (
        <MiniCatchupDetail
          catchupData={previewData.current}
          closeModal={() => {
            setShowMiniCatchupDetail(false);
          }}
          isFirstRow={isFirstRow.current}
          isRight={isRight.current}
          isLeft={isLeft.current}
        />
      ) : null}
    </div>
  );
};

export default CatchupGridView;